/**
 * Mục lục phần khảo sát + theo dõi vị trí cuộn (port thanh `rail` của bản mẫu,
 * `buildRail()`/`observeSections()` ~dòng 2643-2670).
 *
 * Phiếu `onfarm` có 12 phần và gần 180 câu hỏi; không có mục lục thì điều tra
 * viên phải cuộn mò giữa các phần khi hộ trả lời không theo thứ tự.
 */
import { el, esc } from './util.js';

/**
 * @param {HTMLElement} host
 * @param {{getSections: () => Array<{id:string,num:string,text:string,el:HTMLElement}>}} renderer
 * @param {{onSelect?: (section: object) => boolean}} [opts] - `onSelect` trả `true` là đã tự xử lý
 *   (đang ở bản xem lại thì cuộn trong bản đó, không phải trong màn nhập đang ẩn).
 * @returns {{ refresh: () => void, setBadges: (states: Map) => void, destroy: () => void }}
 */
export function createFormRail(host, renderer, { onSelect } = {}) {
  let observer = null;

  function build() {
    const sections = renderer.getSections();
    host.innerHTML = '';
    if (!sections.length) {
      host.hidden = true;
      return;
    }
    host.hidden = false;
    host.setAttribute('aria-label', 'Mục lục phần khảo sát');

    sections.forEach((s) => {
      const item = el('button', 'rail-item');
      item.type = 'button';
      item.dataset.target = s.id;
      item.innerHTML = `<span class="rail-num">${esc(s.num || '•')}</span><span class="rail-text">${esc(s.text)}</span><span class="rail-badges"></span>`;
      item.addEventListener('click', () => {
        if (onSelect?.(s)) return;
        // Mở phần đang gập lại trước khi cuộn tới, nếu không người dùng bấm vào
        // mục lục mà màn hình chỉ nhảy tới một cái tiêu đề đóng.
        s.el.classList.remove('collapsed');
        s.el.querySelector('.form-section-head')?.setAttribute('aria-expanded', 'true');
        s.el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
      host.appendChild(item);
    });

    observer?.disconnect();
    observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          host.querySelectorAll('.rail-item').forEach((b) => {
            b.classList.toggle('active', b.dataset.target === entry.target.id);
          });
        });
      },
      // Dải hẹp quanh 1/4 trên màn hình: phần "đang xem" là phần có tiêu đề vừa
      // trôi qua vùng đó, không phải phần chiếm nhiều pixel nhất.
      { rootMargin: '-15% 0px -70% 0px', threshold: 0 },
    );
    sections.forEach((s) => observer.observe(s.el));
  }

  build();

  return {
    refresh: build,
    /** Số mục còn thiếu (đỏ) và cảnh báo (vàng) của từng phần; phần không có trong `states` đang ẩn. */
    setBadges(states) {
      host.querySelectorAll('.rail-item').forEach((item) => {
        const state = states.get(item.dataset.target);
        const parts = [];
        if (state?.missing) parts.push(`<span class="rail-dot is-error" title="Còn ${state.missing} mục chưa đủ">${state.missing}</span>`);
        if (state?.warnings) parts.push(`<span class="rail-dot is-warn" title="${state.warnings} cảnh báo">${state.warnings}</span>`);
        item.querySelector('.rail-badges').innerHTML = parts.join('');
      });
    },
    destroy() {
      observer?.disconnect();
      observer = null;
      host.innerHTML = '';
    },
  };
}
